import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { trackEvent } from '../utils/analytics';
import useSEO from '../hooks/useSEO';
import { WELCOME_SEEN_KEY } from '../constants/welcome';

// One job: birthday in, name certificate out. This is where QR codes and event signage point
// (/afrifest, /certificate), so it skips the welcome doorway and the pop-ups and asks for
// nothing but a birthday. Someone standing at a stall gives a page about thirty seconds.
//
// The palette is taken from a Namib salt pan: pale sky at the top, salt-white ground, a
// terracotta dune on the horizon and charcoal from the dead acacias for type. Buttons and
// labels keep the brand gold so the page still reads as Mama Africa.

// A real output of the certificate generator (Ama, Saturday-born), saved as a small image so
// phones on festival data are not asked to draw a 5760px canvas just to show an example.
// Regenerate it if the certificate design changes.
const SAMPLE = '/images/certificate-sample-royal-ama.webp';

/** Event routes tag the visit so scans can be counted; /certificate is the everyday version. */
const SOURCES = { '/afrifest': 'afrifest' };

const STEPS = [
  ['Enter your birthday', 'We find the day of the week you were born.'],
  ['Meet your Akan name', 'Kwasi, Ama, Kofi… and what it says about you.'],
  ['Get your certificate', 'Pick a style, then download or share it on WhatsApp.'],
];

export default function CertificatePage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const source = SOURCES[pathname] || 'certificate_page';
  const isEvent = source !== 'certificate_page';
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState('female');
  const [error, setError] = useState('');

  useSEO({
    title: 'Free Akan Name Certificate',
    description: 'Enter your birthday to discover your Ghanaian Akan day name and get your free personalised name certificate.',
    image: '/gilded-african-heritage-share.jpg',
  });

  useEffect(() => {
    trackEvent('certificate_page_view', { source });
    // Arriving here counts as having been welcomed: the next visit to / goes to the home page.
    try { sessionStorage.setItem(WELCOME_SEEN_KEY, '1'); } catch { /* storage blocked */ }
  }, [source]);

  function handleSubmit(e) {
    e.preventDefault();
    if (!dob) {
      setError('Please choose your birthday.');
      return;
    }
    trackEvent('start_name_reveal', { gender, source });
    navigate(`/result?dob=${dob}&gender=${gender}&cert=1&src=${source}`);
  }

  return (
    <div className="page-wrapper af-page">
      <section className="af-hero">
        <div className="af-grid">
          <div className="af-intro">
            {isEvent && <p className="af-badge">Akwaaba · Afrifest Houston</p>}
            <h1 className="af-title">
              What is your <em>Akan name?</em>
            </h1>
            <p className="af-lede">
              In Ghana, your name comes from the day you were born. Enter your birthday and receive
              your <strong>free personalised name certificate</strong>.
            </p>

            <form onSubmit={handleSubmit} className="af-card" noValidate>
              <label className="af-label" htmlFor="cert-dob">Your birthday</label>
              <div className="af-date">
                <input
                  id="cert-dob"
                  type="date"
                  value={dob}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => { setDob(e.target.value); setError(''); }}
                  className={dob ? 'has-value' : ''}
                />
                {/* iPhone shows an empty date field as a blank box, so say what it is for. */}
                {!dob && <span className="af-date-hint" aria-hidden="true">Tap to choose your birthday</span>}
              </div>

              <span className="af-label" id="cert-gender">Gender</span>
              <div className="af-toggle" role="group" aria-labelledby="cert-gender">
                {['female', 'male'].map((g) => (
                  <button
                    key={g}
                    type="button"
                    aria-pressed={gender === g}
                    className={gender === g ? 'is-active' : ''}
                    onClick={() => setGender(g)}
                  >
                    {g === 'female' ? 'Female' : 'Male'}
                  </button>
                ))}
              </div>

              {error && <p className="af-error" role="alert">{error}</p>}

              <button type="submit" className="af-submit">Get my name certificate</button>
              <p className="af-note">Free · takes about 10 seconds</p>
            </form>
          </div>

          <figure className="af-sample">
            <div className="af-mat">
              <img src={SAMPLE} alt="Sample Akan name certificate for Ama, a Saturday-born girl" />
            </div>
            <figcaption>Your name, your day, your meaning, ready to download and share.</figcaption>
          </figure>
        </div>

        {/* The dune on the horizon: a quiet divider in the colour of the Namib sand. */}
        <svg className="af-dune" viewBox="0 0 1440 120" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="afDune" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#B85A30" />
              <stop offset="100%" stopColor="#A9502A" />
            </linearGradient>
          </defs>
          <path d="M0 120 L0 70 C 220 18, 470 8, 720 52 C 860 76, 960 40, 1110 30 C 1250 22, 1360 44, 1440 58 L1440 120 Z" fill="url(#afDune)" />
        </svg>
      </section>

      <section className="af-steps-wrap">
        <ol className="af-steps">
          {STEPS.map(([title, body], i) => (
            <li key={title}>
              <span className="af-step-num">0{i + 1}</span>
              <p className="af-step-title">{title}</p>
              <p className="af-step-body">{body}</p>
            </li>
          ))}
        </ol>
      </section>

      <style>{`
        .af-page {
          --af-sky: #DCE6F3;
          --af-salt: #F4EEE4;
          --af-sand: #EBE0CF;
          --af-dune: #A9502A;
          --af-ink: #2C2621;
          --af-ink-soft: #6A5C4F;
          --af-line: #E2D4BE;
          --af-gold: #C9A558;
          --af-gold-light: #E8CB82;
          --af-gold-deep: #8F6A2C;
          background: var(--af-salt);
          color: var(--af-ink);
        }
        .af-hero {
          position: relative;
          padding: clamp(36px,6vw,80px) 16px clamp(96px,10vw,140px);
          background: linear-gradient(180deg, var(--af-sky) 0%, var(--af-salt) 42%, var(--af-salt) 100%);
          overflow: hidden;
        }
        .af-grid {
          position: relative; z-index: 1;
          max-width: 1080px; margin: 0 auto; display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(100%, 340px), 1fr));
          gap: clamp(32px,5vw,64px); align-items: center;
        }
        .af-intro { text-align: center; }
        .af-badge {
          display: inline-block; margin: 0 0 18px; padding: 6px 16px; border-radius: 999px;
          border: 1px solid var(--af-gold); background: rgba(255,255,255,0.55);
          font: 600 11px/1.4 'Montserrat', sans-serif; letter-spacing: 0.2em;
          color: var(--af-gold-deep); text-transform: uppercase;
        }
        .af-title {
          font: 700 clamp(34px,6vw,56px)/1.08 'Playfair Display', serif;
          color: var(--af-ink); margin: 0 0 16px; letter-spacing: -0.01em;
        }
        .af-title em { font-style: italic; color: var(--af-dune); }
        .af-lede {
          font: 400 clamp(17px,2.3vw,20px)/1.65 'EB Garamond', serif;
          color: var(--af-ink-soft); max-width: 460px; margin: 0 auto 28px;
        }
        .af-lede strong { color: var(--af-ink); font-weight: 600; }

        .af-card {
          max-width: 440px; margin: 0 auto; text-align: left;
          background: #FFFDF9; border: 1px solid var(--af-line); border-radius: 16px;
          padding: clamp(22px,4vw,30px);
          box-shadow: 0 1px 0 rgba(255,255,255,0.8) inset, 0 18px 40px rgba(110,80,40,0.10);
        }
        .af-label {
          display: block; margin: 0 0 10px;
          font: 600 11px/1.3 'Cinzel', serif; letter-spacing: 0.2em;
          color: var(--af-gold-deep); text-transform: uppercase;
        }
        .af-date { position: relative; margin-bottom: 22px; }
        .af-date input {
          display: block; width: 100%; min-height: 52px; box-sizing: border-box;
          -webkit-appearance: none; appearance: none;
          padding: 0 16px; border-radius: 10px;
          border: 1px solid #D9C8AC; background: #FAF6EF;
          font: 500 17px/52px 'Montserrat', sans-serif; color: var(--af-ink);
          color-scheme: light; text-align: left;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .af-date input::-webkit-date-and-time-value { text-align: left; }
        .af-date input:not(.has-value) { color: transparent; }
        .af-date input:focus { outline: none; border-color: var(--af-gold); box-shadow: 0 0 0 3px rgba(201,165,88,0.22); }
        .af-date-hint {
          position: absolute; left: 16px; top: 50%; transform: translateY(-50%);
          pointer-events: none; color: #9A8A78;
          font: italic 400 17px/1 'EB Garamond', serif;
        }

        .af-toggle { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 24px; }
        .af-toggle button {
          min-height: 48px; border-radius: 10px; cursor: pointer;
          border: 1px solid #D9C8AC; background: #FFFFFF; color: var(--af-ink-soft);
          font: 600 13px/1 'Cinzel', serif; letter-spacing: 0.16em; text-transform: uppercase;
          transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
        }
        .af-toggle button:hover { border-color: var(--af-gold); }
        .af-toggle button.is-active {
          background: linear-gradient(135deg, var(--af-gold), var(--af-gold-light));
          border-color: var(--af-gold); color: #1C0E04;
        }

        .af-error { margin: -8px 0 16px; color: #A3361E; font: 500 14px/1.4 'Montserrat', sans-serif; }

        .af-submit {
          width: 100%; min-height: 54px; border: none; border-radius: 10px; cursor: pointer;
          background: linear-gradient(135deg, var(--af-gold) 0%, var(--af-gold-light) 100%);
          color: #1C0E04; font: 700 14px/1.2 'Cinzel', serif; letter-spacing: 0.16em;
          text-transform: uppercase; padding: 0 18px;
          box-shadow: 0 10px 22px rgba(143,106,44,0.25);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .af-submit:hover { transform: translateY(-1px); box-shadow: 0 14px 28px rgba(143,106,44,0.32); }
        .af-note { margin: 12px 0 0; text-align: center; color: var(--af-ink-soft); font: 400 13px/1.4 'Montserrat', sans-serif; letter-spacing: 0.04em; }

        .af-sample { margin: 0; text-align: center; }
        .af-mat {
          background: #FFFFFF; padding: clamp(8px,1.4vw,14px); border-radius: 14px;
          border: 1px solid var(--af-line);
          box-shadow: 0 24px 50px rgba(110,80,40,0.16), 0 2px 6px rgba(110,80,40,0.06);
        }
        .af-mat img { display: block; width: 100%; height: auto; border-radius: 6px; }
        .af-sample figcaption {
          margin-top: 14px; color: var(--af-ink-soft);
          font: italic 400 16px/1.5 'EB Garamond', serif;
        }

        .af-dune { position: absolute; left: 0; right: 0; bottom: -1px; width: 100%; height: clamp(60px,9vw,120px); display: block; }

        .af-steps-wrap { background: var(--af-dune); padding: 8px 16px clamp(40px,6vw,64px); }
        .af-steps {
          list-style: none; padding: 0; margin: 0 auto; max-width: 960px;
          display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr)); gap: 18px;
        }
        .af-steps li {
          background: rgba(255,250,242,0.08); border: 1px solid rgba(255,240,220,0.22);
          border-radius: 12px; padding: 18px 18px 16px;
        }
        .af-step-num { font: 600 13px/1 'Cinzel', serif; letter-spacing: 0.14em; color: var(--af-gold-light); }
        .af-step-title { margin: 10px 0 6px; font: 600 16px/1.3 'Cinzel', serif; color: #FFF6EA; letter-spacing: 0.04em; }
        .af-step-body { margin: 0; font: 400 16px/1.5 'EB Garamond', serif; color: rgba(255,244,230,0.86); }
      `}</style>
    </div>
  );
}
